import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { QuestionOption, QuestionType } from '@/services/certification.service';

interface QuestionOptionsFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  error?: string;
  required?: boolean;
  questionType: QuestionType;
  minOptions?: number;
}

export function QuestionOptionsField<T extends FieldValues>({
  name,
  control,
  label = 'Answer Options',
  error,
  required = false,
  questionType,
  minOptions = 2,
}: QuestionOptionsFieldProps<T>) {
  return (
    <div className="w-full">
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const options = (field.value as QuestionOption[]) || [];

          const addOption = () => {
            const newOption: QuestionOption = {
              id: `opt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              text: '',
              is_correct: false,
            };
            field.onChange([...options, newOption]);
          };

          const removeOption = (index: number) => {
            field.onChange(options.filter((_, i) => i !== index));
          };

          const updateOption = (index: number, updates: Partial<QuestionOption>) => {
            const updated = options.map((opt, i) =>
              i === index ? { ...opt, ...updates } : opt
            );
            field.onChange(updated);
          };

          const handleCorrectChange = (index: number, checked: boolean) => {
            if (questionType === 'SINGLE_CHOICE' || questionType === 'TRUE_FALSE') {
              // For single choice, uncheck all others
              const updated = options.map((opt, i) => ({
                ...opt,
                is_correct: i === index ? checked : false,
              }));
              field.onChange(updated);
            } else {
              // For multiple choice, toggle the specific option
              updateOption(index, { is_correct: checked });
            }
          };

          const maxOptions = questionType === 'TRUE_FALSE' ? 2 : Infinity;
          const canAddOption = options.length < maxOptions;

          return (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  {label}
                  {required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <button
                  type="button"
                  onClick={addOption}
                  disabled={!canAddOption}
                  className={`text-sm font-semibold flex items-center gap-1 transition-colors ${
                    canAddOption
                      ? 'text-brand-indigo-600 hover:text-brand-indigo-700'
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Add Option
                </button>
              </div>

              <div className="space-y-3">
                {options.map((option, index) => (
                  <div
                    key={option.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    {/* Correct Answer Checkbox/Radio */}
                    <div className="flex items-center">
                      <input
                        type={questionType === 'MULTIPLE_CHOICE' ? 'checkbox' : 'radio'}
                        name={`correct_option_${name}`}
                        checked={option.is_correct}
                        onChange={(e) => handleCorrectChange(index, e.target.checked)}
                        className="w-4 h-4 text-brand-indigo-600 focus:ring-2 focus:ring-brand-indigo-500 cursor-pointer"
                        title="Mark as correct answer"
                      />
                    </div>

                    {/* Option Text Input */}
                    <div className="flex-1">
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => updateOption(index, { text: e.target.value })}
                        placeholder={`Option ${String.fromCharCode(65 + index)}`}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-brand-indigo-500 focus:border-brand-indigo-500 transition-all"
                      />
                    </div>

                    {/* Remove Button */}
                    {options.length > minOptions && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove option"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Error Message */}
              {error && <p className="text-red-500 text-xs mt-2">{error}</p>}

              {/* Validation Hint */}
              {options.length === 0 && (
                <p className="text-gray-500 text-xs mt-2">
                  Add at least {minOptions} options
                </p>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
